import { Navigate, Outlet, Route } from 'react-router-dom';
import { useProdFeatureFlagEnabled } from '~/common/hooks/useProdFeatureFlagEnabled';
import { SentryRoutes } from '~/common/utils/sentry-routes';
import { Authenticated } from '~/services/main/components/auth/Authenticated';
import { ConsumerApp } from './ConsumerApp';
import { EmbeddedDashboardLayout } from './components/Layouts/EmbeddedDashboardLayout';
import { AccountPage, AgentProfilePage } from './pages/Account';
import { ActivityPage } from './pages/Activity';
import { AgentComplianceReviewPage } from './pages/AgentComplianceReview';
import { AgentSignupV2Page } from './pages/AgentSignupV2';
import {
  BOABuyerViewPage,
  BuyerOfferAnalysisPage,
} from './pages/BuyerOfferAnalysis';
import {
  BuyerInfoPage,
  BuyerOfferScorecardsPage,
  BuyersPage,
} from './pages/Buyers';
import { BuyerPartyPageLayout } from './pages/Buyers/BuyerPartyPageLayout';
import { NotFoundPage } from './pages/Errors';
import { InvitationAcceptPage } from './pages/Invitation';
import {
  ActiveListingsPage,
  ListingPage,
  ListingsPage,
  PastListingsPage,
  SharedListingPage,
} from './pages/Listings';
import { MainLayout } from './pages/MainLayout';
import { MapSearchPage } from './pages/MapSearch';
import { OfferScorecardsPage } from './pages/OfferScorecards';
import { OrgAdminRedirect } from './pages/OrgAdminRedirectRoute';
import {
  AccessControlsPage,
  DataCoachChatPage,
  DemoSalesPipelineComplianceDrawer,
  LenderTemplatesPage,
  NotificationSettingsPage,
  NotificationTemplateModal,
  NotificationsListPage,
  NotificationsPipelinePage,
  NotificationsRosterPage,
  OfficesPage,
  OrganizationAdminPage,
  OrganizationComplianceDashboardDemoPage,
  OrganizationComplianceDashboardPage,
  OrganizationComplianceDashboardSettings,
  OrganizationComplianceGroupStageMapping,
  OrganizationCompliancePage,
  OrganizationDarwinSettings,
  OrganizationDemoPipelinePage,
  OrganizationEssentialsPerformanceDemoPage,
  OrganizationFlexPerformanceDemoPage,
  OrganizationIntegrationConnections,
  OrganizationKeyTermSettings,
  OrganizationMortgagePerformanceDemoPage,
  OrganizationNotificationsPage,
  OrganizationOfferBenchmarksPage,
  OrganizationOfferScorecardsPage,
  OrganizationPageLayout,
  OrganizationPerformancePage,
  OrganizationPipelinePage,
  OrganizationProfitPowerTeamsPage,
  PartnerNotificationsPage,
} from './pages/Organizations';
import { PublicLayout } from './pages/PublicLayout';
import { StorefrontPage } from './pages/Storefront';
import { LoginPage, LogoutPage } from './pages/auth';

export const Routes = () => {
  const isListingReportEnabled = useProdFeatureFlagEnabled('listing-report');
  const isOffMarketListingsEnabled = useProdFeatureFlagEnabled(
    'off-market-listings'
  );
  const isDemoEnvironment = window.ENVS.environment !== 'production';
  // const [searchParams] = useSearchParams();

  // const offerFlowRoutes = useCallback(
  //   () => (
  //     <>
  //       <Route index element={<Navigate replace to="contract" />} />
  //       <Route Component={BuySideUploadedOfferReviewPage} path="uploaded" />
  //       <Route Component={ChooseContractPage} path="contract" />
  //       <Route Component={KeyTermsPage} path="key-terms" />
  //       <Route Component={BuyerPage} path="buyer" />
  //       <Route Component={AddDocsPage} path="add-docs" />
  //       <Route Component={FillDocsPage} path="fill-docs" />
  //       <Route Component={ReviewPage} path="review">
  //         <Route
  //           Component={PrepareForBuyerSignature}
  //           path="prepare-for-signature"
  //         />
  //       </Route>
  //     </>
  //   ),
  //   []
  // );

  return (
    <SentryRoutes>
      <Route Component={ConsumerApp} path={window.ENVS?.baseRouteUrl || '/'}>
        {/* Auth Routes */}
        <Route Component={LoginPage} path="auth/login" />
        <Route Component={LogoutPage} path="auth/logout" />
        {/* <Route Component={LoginForInvitedSellerPage} path="i/:invitationId" /> */}
        <Route Component={AgentSignupV2Page} path="auth/signup">
          <Route Component={Outlet} path=":step" />
        </Route>

        {/* Public Routes */}
        {/* <Route
          Component={ListingPackageInstructions}
          path="listing-package/:propertySlug"
        /> */}

        <Route Component={PublicLayout} path="/">
          {/* <Route Component={EsignPage} path="esign/:code" /> */}
          <Route Component={AgentProfilePage} path="agent/:userName" />
          <Route Component={StorefrontPage} path="storefront/:propertySlug">
            {/* <Route index Component={StartOfferPage} />
            <Route Component={StorefrontOfferFlowPage} path="offer">
              <Route index element={<OfferProcessingAnimation />} />
              <Route Component={OfferFlowLayout} path="">
                {offerFlowRoutes()}
              </Route>
            </Route> */}
          </Route>
          {/* <Route
            Component={OpenHouseGuestBookSignPageLayout}
            errorElement={<NotFoundPage />}
            path="open-house/:openHouseId"
          >
            <Route index Component={OpenHouseGuestBookSignIndexPage} />
            <Route Component={OpenHouseGuestBookSignFormsPage} path="sign" />
          </Route> */}
          <Route
            Component={BOABuyerViewPage}
            path="offer-scorecard/:offerScorecardId/:token"
          />
          {/* SharedPage mounts once for the folder. The path carries only the
              folder (matching `compliance/:folderId`); the share-link token and
              the open document both ride in the query string
              (`?token=…&documentId=…`). */}
          <Route
            Component={AgentComplianceReviewPage}
            path="compliance-review/:ingestionFolderUuid"
          />
          {isListingReportEnabled && (
            <Route
              Component={SharedListingPage}
              path="listing/:propertySlug/:token"
            />
          )}
          {isListingReportEnabled && isOffMarketListingsEnabled && (
            <Route
              Component={SharedListingPage}
              path="off-market-listing/:propertyDetailsId/:token"
            />
          )}
        </Route>

        <Route
          Component={InvitationAcceptPage}
          path="invitations/accept/:code"
        />

        {/* Authenticated Routes */}
        <Route Component={Authenticated}>
          <Route element={<Navigate replace to="/map" />} path="storefront" />
          <Route element={<Navigate replace to="/map" />} path="search" />
          <Route element={<Navigate replace to="/map" />} path="dashboard" />
          <Route Component={MapSearchPage} path="map" />

          <Route Component={MainLayout} path="/">
            <Route index Component={OrgAdminRedirect} />
            {/* <Route index element={<Navigate replace to="dashboard" />} />
            <Route Component={MainDashboardPage} path="dashboard">
              <Route
                Component={AgencyAgreementContinuePage}
                path="agency-agreement/:indigoContractId"
              />
            </Route> */}
            {/* <Route Component={OfferRedirectPage} path="offers/:offerId" /> */}

            {isOffMarketListingsEnabled && (
              <Route Component={ListingsPage} path="listings">
                <Route element={<Navigate replace to="active" />} path="" />
                <Route Component={ActiveListingsPage} path="active" />
                <Route Component={PastListingsPage} path="past" />
              </Route>
            )}

            <Route Component={OfferScorecardsPage} path="offer-scorecards" />

            {/* Organization Routes */}
            <Route Component={OrganizationPageLayout} path="organizations">
              <Route index element={<Navigate replace to="admin" />} />
              {/* <Route Component={OrganizationOverviewPage} path="overview" /> */}
              <Route Component={OrganizationPipelinePage} path="pipeline">
                <Route
                  Component={DemoSalesPipelineComplianceDrawer}
                  path=":folderId"
                />
              </Route>
              {isDemoEnvironment && (
                <Route
                  Component={OrganizationDemoPipelinePage}
                  path="pipeline-demo"
                >
                  <Route
                    Component={DemoSalesPipelineComplianceDrawer}
                    path=":folderId"
                  />
                </Route>
              )}
              <Route
                Component={OrganizationOfferScorecardsPage}
                path="offer-scorecards"
              />
              {isDemoEnvironment && (
                <Route
                  Component={OrganizationMortgagePerformanceDemoPage}
                  path="mortgage-performance-demo"
                />
              )}
              {isDemoEnvironment && (
                <Route
                  Component={OrganizationEssentialsPerformanceDemoPage}
                  path="essentials-performance-demo"
                />
              )}
              {isDemoEnvironment && (
                <Route
                  Component={OrganizationFlexPerformanceDemoPage}
                  path="flex-performance-demo"
                />
              )}
              <Route
                Component={OrganizationComplianceDashboardPage}
                path="compliance-dashboard"
              />
              {isDemoEnvironment && (
                <Route
                  Component={OrganizationComplianceDashboardDemoPage}
                  path="compliance-dashboard-demo"
                />
              )}
              <Route Component={OrganizationAdminPage} path="admin">
                <Route index element={<Navigate replace to="members" />} />
                <Route Component={AccessControlsPage} path="members" />
                <Route Component={OfficesPage} path="offices" />
                <Route
                  Component={OrganizationIntegrationConnections}
                  path="integration-connections"
                />
                <Route
                  Component={OrganizationKeyTermSettings}
                  path="key-terms"
                />
                <Route
                  Component={OrganizationDarwinSettings}
                  path="darwin-settings"
                />
                <Route
                  Component={OrganizationComplianceDashboardSettings}
                  path="compliance-dashboard-settings"
                />
                <Route
                  Component={OrganizationProfitPowerTeamsPage}
                  path="profit-power-teams"
                />
                <Route
                  element={<Navigate replace to="../darwin-settings" />}
                  path="darwin-company-mapping"
                />
                <Route
                  Component={OrganizationComplianceGroupStageMapping}
                  path="validation-routing"
                />
              </Route>
              <Route
                Component={OrganizationPerformancePage}
                path="performance"
              />
              <Route Component={OrganizationCompliancePage} path="compliance">
                <Route
                  Component={OrganizationCompliancePage}
                  path=":folderId"
                />
              </Route>
              <Route
                Component={OrganizationNotificationsPage}
                path="notifications"
              >
                <Route
                  index
                  element={<Navigate replace to="notifications" />}
                />
                <Route Component={NotificationsPipelinePage} />
                <Route
                  Component={NotificationsPipelinePage}
                  path="notifications"
                />
                <Route Component={NotificationsRosterPage} path="roster">
                  {/* Lender roster: the selected contact rides in the URL. */}
                  <Route index element={null} />
                  <Route element={null} path=":contactId" />
                </Route>
                <Route Component={NotificationsListPage} path="activities" />
                <Route Component={NotificationSettingsPage} path="settings" />
                <Route Component={LenderTemplatesPage} path="config" />
                <Route
                  Component={PartnerNotificationsPage}
                  path="partners/:partnerId"
                >
                  <Route index element={<Navigate replace to="buy" />} />
                  <Route path="buy">
                    <Route index element={null} />
                    <Route Component={NotificationTemplateModal} path="new" />
                    <Route
                      Component={NotificationTemplateModal}
                      path="edit/:notificationTemplateId"
                    />
                  </Route>
                  <Route path="sell">
                    <Route index element={null} />
                    <Route Component={NotificationTemplateModal} path="new" />
                    <Route
                      Component={NotificationTemplateModal}
                      path="edit/:notificationTemplateId"
                    />
                  </Route>
                  <Route element={null} path="global" />
                  <Route path="roster">
                    <Route index element={null} />
                    <Route element={null} path=":contactId" />
                  </Route>
                  <Route path="admin-contacts">
                    <Route index element={null} />
                    <Route element={null} path=":contactId" />
                  </Route>
                </Route>
              </Route>
              <Route Component={DataCoachChatPage} path="chat">
                <Route Component={DataCoachChatPage} path=":threadId" />
              </Route>
            </Route>
            <Route
              Component={EmbeddedDashboardLayout}
              path="organizations/offer-benchmarks"
            >
              <Route index Component={OrganizationOfferBenchmarksPage} />
            </Route>

            {/* Buyers Page */}
            <Route Component={BuyersPage} path="buyers" />
            <Route Component={BuyerPartyPageLayout} path="buyers/:buyerPartyId">
              <Route
                index
                element={<Navigate replace to="offer-scorecards" />}
              />
              <Route
                Component={BuyerOfferScorecardsPage}
                path="offer-scorecards"
              />
              {/* <Route
                index
                element={<Navigate replace to="purchase-offers" />}
              />
              <Route Component={PartyPageWrapper} path="purchase-offers">
                <Route Component={BuyerOfferDetailPage} path=":offerId">
                  <Route Component={OfferFlowLayout} path="">
                    {offerFlowRoutes()}
                  </Route>
                </Route>
              </Route> */}
              <Route Component={BuyerInfoPage} path="buyer-info" />
              {/* <Route
                Component={AgencyAgreementsPage}
                path="agency-agreements"
              /> */}
            </Route>
            <Route Component={AccountPage} path="account" />
            <Route Component={ActivityPage} path="alerts" />
          </Route>

          {/* <Route Component={MainLayoutWithoutDefaultMobileNav} path="/">
            <Route
              Component={ListingSetupPageWrapper}
              path="listings/:listingId"
            >
              <Route
                index
                element={
                  <Navigate
                    replace
                    to={{
                      pathname: 'dashboard',
                      search: searchParams.toString(),
                    }}
                  />
                }
              />
              <Route Component={DashboardPage} path="dashboard" />
              <Route Component={ListingOffersPage} path="offers">
                <Route Component={ListingOfferDetailPage} path=":offerId">
                  <Route Component={OfferFlowLayout} path="">
                    <Route Component={ListingOfferReviewPage} path="" />
                  </Route>
                </Route>
              </Route>
              <Route Component={GuestbookSignersPage} path="guestbook" />
              <Route Component={OpenHousesPage} path="open-houses" />
              <Route Component={AdminsPage} path="admins" />
              <Route Component={SellerPreferencesPage} path="preferences" />
              <Route Component={ComparablesPage} path="comparables" />
              <Route Component={DocumentsPage} path="documents" />
              <Route
                Component={OfferTransparencyPage}
                path="offer-transparency"
              />
            </Route>
          </Route>
          <Route
            Component={StorefrontSetupWizard}
            path="storefront-setup/:listingId"
          >
            <Route index Component={IntroPage} />
            <Route
              Component={StorefrontSetupWizardSellerPreferencesPage}
              path="seller-preferences"
            />
            <Route
              Component={StorefrontSetupWizardComparablesPage}
              path="comparables"
            />
            <Route
              Component={StorefrontSetupWizardDocumentsPage}
              path="documents"
            />
            <Route Component={StorefrontSetupWizardSharePage} path="share" />
          </Route> */}

          {/* Buyer Offer Analysis Routes */}
          <Route
            Component={BuyerOfferAnalysisPage}
            path="offer-scorecard/:offerScorecardId"
          />

          {/* Listing Report Routes */}
          {isListingReportEnabled && (
            <Route Component={ListingPage} path="listing/:propertySlug" />
          )}
          {isOffMarketListingsEnabled && isListingReportEnabled && (
            <Route
              Component={ListingPage}
              path="off-market-listing/:propertyDetailsId"
            />
          )}
          {/* Preview Routes */}
          {isListingReportEnabled && (
            <Route
              Component={SharedListingPage}
              path="listing/:propertySlug/preview"
            />
          )}
          {isListingReportEnabled && isOffMarketListingsEnabled && (
            <Route
              Component={SharedListingPage}
              path="off-market-listing/:propertyDetailsId/preview"
            />
          )}
        </Route>
      </Route>

      {/* Errors */}
      <Route Component={PublicLayout} path="/">
        <Route Component={NotFoundPage} path="*" />
      </Route>
    </SentryRoutes>
  );
};
